import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import { recordReceiptPrint, type Receipt } from './cashdeskApi'
import { fitReceiptPage, receiptImagesSettled } from './receiptPrint'
import { ReceiptPrintPortal } from './ReceiptSlip'

interface PrintJob {
  receipt: Receipt
  copy: boolean
}

export interface ReceiptPrinter {
  print: (receipt: Receipt) => Promise<void>
  printingId: string | null
  error: unknown
  clearError: () => void
  portal: ReactNode
}

export function useReceiptPrinter(): ReceiptPrinter {
  const queryClient = useQueryClient()
  const [job, setJob] = useState<PrintJob | null>(null)
  const [printingId, setPrintingId] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    if (job === null) return
    let cancelled = false
    const run = async () => {
      await new Promise((resolve) => requestAnimationFrame(resolve))
      await receiptImagesSettled()
      if (cancelled) return
      fitReceiptPage()
      window.print()
      setJob(null)
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [job])

  const print = useCallback(
    async (receipt: Receipt) => {
      setError(null)
      setPrintingId(receipt.id)
      try {
        const { copy } = await recordReceiptPrint(receipt.id)
        setJob({ receipt, copy })
        void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.receipts })
      } catch (failure) {
        setError(failure)
      } finally {
        setPrintingId(null)
      }
    },
    [queryClient],
  )

  return {
    print,
    printingId,
    error,
    clearError: () => setError(null),
    portal: job ? <ReceiptPrintPortal receipt={job.receipt} copy={job.copy} /> : null,
  }
}
